/**
 * TabDndContext — provides @dnd-kit DnD context for tab dragging.
 *
 * Uses Pointer sensor (not HTML5 DnD) for reliable cross-pane tab moves,
 * especially in WKWebView / Tauri.
 *
 * Wrap this around the area containing all panes and tab bars.
 */

import {
    type CollisionDetection,
    closestCenter,
    DndContext,
    type DragEndEvent,
    DragOverlay,
    type DragStartEvent,
    PointerSensor,
    rectIntersection,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { FileText } from "lucide-react";
import {
    createContext,
    type ReactNode,
    useCallback,
    useContext,
    useRef,
    useState,
} from "react";
import { useEditorStore } from "../store/editor-store";

/* ── Custom collision detection ──
 *
 * Each pane has a large `useDroppable` container (pane-tab-bar) AND
 * individual `useSortable` items (tabs) inside it. The default
 * rectIntersection prefers the larger container, so reordering events
 * never fire. We first check sortable items (closestCenter) and fall
 * back to container droppables (rectIntersection) for cross-pane drops.
 */
const tabCollisionDetection: CollisionDetection = (args) => {
    // Separate sortable items from plain droppable containers
    const sortableContainers = args.droppableContainers.filter(
        (c) => c.data.current?.sortable != null,
    );

    if (sortableContainers.length > 0) {
        const hits = closestCenter({
            ...args,
            droppableContainers: sortableContainers,
        });
        if (hits.length > 0) return hits;
    }

    // Fall back to container droppables (pane-tab-bar backgrounds)
    return rectIntersection(args);
};

/* ── Shared drag state (readable by any descendant) ── */

interface TabDndState {
    /** The tab ID currently being dragged, or null */
    activeTabId: string | null;
    /** The pane the tab was dragged from, or null */
    sourcePaneId: string | null;
}

const TabDndStateContext = createContext<TabDndState>({
    activeTabId: null,
    sourcePaneId: null,
});
export function useTabDndState() {
    return useContext(TabDndStateContext);
}

/* ── Drag data stored in the active draggable ── */

export interface TabDragData {
    type: "tab";
    tabId: string;
    /** paneId the tab is being dragged FROM (empty string for global TabBar) */
    sourcePaneId: string;
}

/* ── Drop data stored in droppable ── */

export interface TabBarDropData {
    type: "pane-tab-bar";
    paneId: string;
}

/* ── Provider ── */

export interface TabDndProviderProps {
    children: ReactNode;
    /**
     * Called when a tab is dropped outside the window bounds.
     * The host app can use this to create a new detached window.
     * Receives the tab ID that was dropped out.
     */
    onDragOutOfWindow?: (tabId: string) => void;
}

export function TabDndProvider({ children, onDragOutOfWindow }: TabDndProviderProps) {
    const setPaneTab = useEditorStore((s) => s.setPaneTab);
    const setFocusedPane = useEditorStore((s) => s.setFocusedPane);
    const removeTabFromPane = useEditorStore((s) => s.removeTabFromPane);
    const reorderPaneTabs = useEditorStore((s) => s.reorderPaneTabs);
    const reorderGlobalTabs = useEditorStore((s) => s.reorderGlobalTabs);

    const [state, setState] = useState<TabDndState>({
        activeTabId: null,
        sourcePaneId: null,
    });

    // ── Pointer tracking for out-of-window detection ──
    // Use a native pointermove listener (attached during drag only) instead
    // of relying on @dnd-kit's DragMoveEvent, which may stop firing when the
    // pointer leaves the webview in Tauri / WKWebView.
    const lastPointerRef = useRef<{ x: number; y: number } | null>(null);
    // Keep a ref to the active drag data so handleDragCancel can access it
    const activeDragRef = useRef<TabDragData | null>(null);

    // Require a small distance before starting drag so clicks still work
    const pointerSensor = useSensor(PointerSensor, {
        activationConstraint: { distance: 5 },
    });
    const sensors = useSensors(pointerSensor);

    const handleDragStart = useCallback((event: DragStartEvent) => {
        const data = event.active.data.current as TabDragData | undefined;
        if (data?.type === "tab") {
            setState({ activeTabId: data.tabId, sourcePaneId: data.sourcePaneId });
            activeDragRef.current = data;
        }
        lastPointerRef.current = null;

        // Attach a native pointermove listener on the window for reliable
        // pointer tracking even when the cursor leaves the webview bounds.
        // Pointer capture (set by @dnd-kit PointerSensor) ensures these
        // events continue flowing on macOS.
        const onPointerMove = (e: PointerEvent) => {
            lastPointerRef.current = { x: e.clientX, y: e.clientY };
        };
        window.addEventListener("pointermove", onPointerMove);
        // Store cleanup so we can remove it on end/cancel
        (handleDragStart as unknown as { _cleanup?: () => void })._cleanup =
            () => window.removeEventListener("pointermove", onPointerMove);
    }, []);

    /** Shared logic: check if the pointer ended outside the window bounds. */
    const checkOutOfWindow = useCallback(
        (tabId: string) => {
            const ptr = lastPointerRef.current;
            if (!ptr || !onDragOutOfWindow) return;
            const margin = 10;
            const isOutside =
                ptr.x < -margin ||
                ptr.y < -margin ||
                ptr.x > window.innerWidth + margin ||
                ptr.y > window.innerHeight + margin;
            if (isOutside) {
                onDragOutOfWindow(tabId);
            }
        },
        [onDragOutOfWindow],
    );

    /** Clean up native listener + reset refs. */
    const cleanupDrag = useCallback(() => {
        (handleDragStart as unknown as { _cleanup?: () => void })._cleanup?.();
        activeDragRef.current = null;
        lastPointerRef.current = null;
    }, [handleDragStart]);

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            const dragData = event.active.data.current as TabDragData | undefined;
            const overData = event.over?.data.current as
                | (TabDragData | TabBarDropData)
                | undefined;

            setState({ activeTabId: null, sourcePaneId: null });
            cleanupDrag();

            if (!dragData || dragData.type !== "tab") return;

            // ── Case 0: No drop target — check if pointer is outside window ──
            if (!overData) {
                checkOutOfWindow(dragData.tabId);
                return;
            }

            const { tabId, sourcePaneId } = dragData;

            // ── Case 1: Dropped on another tab (sortable reorder or cross-pane) ──
            if (overData.type === "tab") {
                const overTabData = overData as TabDragData;
                const targetPaneId = overTabData.sourcePaneId;

                if (sourcePaneId === targetPaneId) {
                    // Same pane — reorder
                    if (sourcePaneId === "" || sourcePaneId === "global") {
                        // Global TabBar reorder
                        const tabs = useEditorStore.getState().tabs;
                        const ids = tabs.map((t) => t.id);
                        const oldIndex = ids.indexOf(tabId);
                        const newIndex = ids.indexOf(overTabData.tabId);
                        if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
                            reorderGlobalTabs(arrayMove(ids, oldIndex, newIndex));
                        }
                    } else {
                        // Pane-specific tab reorder
                        const panes = useEditorStore.getState().panes;
                        const pane = panes.find((p) => p.paneId === sourcePaneId);
                        if (pane) {
                            const ids = pane.tabIds;
                            const oldIndex = ids.indexOf(tabId);
                            const newIndex = ids.indexOf(overTabData.tabId);
                            if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
                                reorderPaneTabs(
                                    sourcePaneId,
                                    arrayMove([...ids], oldIndex, newIndex),
                                );
                            }
                        }
                    }
                } else {
                    // Cross-pane: remove from source, add to target at the drop position
                    if (
                        sourcePaneId &&
                        sourcePaneId !== "" &&
                        sourcePaneId !== "global"
                    ) {
                        removeTabFromPane(sourcePaneId, tabId);
                    }
                    if (
                        targetPaneId &&
                        targetPaneId !== "" &&
                        targetPaneId !== "global"
                    ) {
                        setFocusedPane(targetPaneId);
                        setPaneTab(targetPaneId, tabId);
                    }
                }
                return;
            }

            // ── Case 2: Dropped on a pane tab-bar background (cross-pane move) ──
            if (overData.type === "pane-tab-bar") {
                const dropData = overData as TabBarDropData;
                const targetPaneId = dropData.paneId;

                if (
                    sourcePaneId &&
                    sourcePaneId !== targetPaneId &&
                    sourcePaneId !== "" &&
                    sourcePaneId !== "global"
                ) {
                    removeTabFromPane(sourcePaneId, tabId);
                }
                setFocusedPane(targetPaneId);
                setPaneTab(targetPaneId, tabId);
            }
        },
        [
            checkOutOfWindow,
            cleanupDrag,
            setPaneTab,
            setFocusedPane,
            removeTabFromPane,
            reorderPaneTabs,
            reorderGlobalTabs,
        ],
    );

    const handleDragCancel = useCallback(() => {
        // @dnd-kit fires onDragCancel (instead of onDragEnd) when pointer
        // capture is lost — which commonly happens in Tauri / WKWebView when
        // the pointer leaves the webview bounds. Treat this as a potential
        // out-of-window drop.
        const dragData = activeDragRef.current;
        setState({ activeTabId: null, sourcePaneId: null });

        if (dragData) {
            checkOutOfWindow(dragData.tabId);
        }
        cleanupDrag();
    }, [checkOutOfWindow, cleanupDrag]);

    // Render the dragged tab's title in the overlay
    const activeTab = useEditorStore((s) =>
        state.activeTabId
            ? s.tabs.find((t) => t.id === state.activeTabId)
            : undefined,
    );

    return (
        <TabDndStateContext.Provider value={state}>
            <DndContext
                sensors={sensors}
                collisionDetection={tabCollisionDetection}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
            >
                {children}
                <DragOverlay dropAnimation={null}>
                    {activeTab ? (
                        <div className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-background border border-border rounded shadow-lg opacity-90">
                            <FileText size={12} />
                            <span className="max-w-[120px] truncate">
                                {activeTab.title.replace(/\.md$/i, "")}
                            </span>
                        </div>
                    ) : null}
                </DragOverlay>
            </DndContext>
        </TabDndStateContext.Provider>
    );
}
